import { Box, Button, ButtonProps, Text } from "@vinomofo/components";
import { ReactElement, useEffect, useState } from "react";

import { CartIcon } from "@vinomofo/icons";
import styled, { keyframes } from "styled-components";

export interface CartIconButtonProps extends ButtonProps {
  count?: number;
  isLoading: boolean;
  onClick: () => void;
  isLarge: boolean;
  hasLowStockItem: boolean | undefined;
  disableAnimation: boolean;
}

const CartButton = styled(Button)<{ count: number }>`
  &:hover {
    color: ${({ theme, count }) =>
      count ? theme.colors.primary : theme.colors.white};
  }
`;

const CartDotIndicator = styled(Box)`
  border: 2px solid ${({ theme }) => theme.colors.secondary};
  border-radius: 50%;
  width: 12px;
  height: 12px;
  position: absolute;
  top: -2px;
  right: -4px;
`;

const CartCounter = styled.div<{ hasItems?: boolean }>`
  border: 2px solid
    ${({ theme, hasItems }) =>
      hasItems ? theme.colors.brand : theme.colors.dorianGray};
  color: ${({ theme, hasItems }) =>
    hasItems ? theme.colors.white : theme.colors.dorianGray};
  width: 25px;
  height: 25px;
  border-radius: 50%;
  margin-left: 10px;
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    font-weight: 500;
    color: inherit;
  }
`;

const jumpShakingAnimation = keyframes`
  0% { transform: translateX(0) }
  25% { transform: translateY(-5px) }
  35% { transform: translateY(-5px) rotate(20deg) }
  55% { transform: translateY(-5px) rotate(-20deg) }
  65% { transform: translateY(-5px) rotate(20deg) }
  75% { transform: translateY(-5px) rotate(-20deg) }
  100% { transform: translateY(0) rotate(0) }
`;

const horizontalShakingAnimation = keyframes`
  0% { transform:translate(0,0) }
  1.78571% { transform:translate(5px,0) }
  3.57143% { transform:translate(0,0) }
  5.35714% { transform:translate(5px,0) }
  7.14286% { transform:translate(0,0) }
  8.92857% { transform:translate(5px,0) }
  10.71429% { transform:translate(0,0) }
  100% { transform:translate(0,0) }
`;

const CartIconStyled = styled(CartIcon)`
  &.shake-animation {
    animation: ${jumpShakingAnimation} 1s infinite;

    @media ${({ theme }) => theme.mq.max.mobile} {
      animation: ${horizontalShakingAnimation} 4.72s ease infinite;
      transform-origin: 50% 50%;
    }
  }
`;

const CartTotalIndicator = ({
  isLoading,
  count = 0,
}: {
  isLoading: boolean;
  count?: number;
}): ReactElement => {
  return (
    <CartCounter hasItems={!!count}>
      {!isLoading && (
        <Box>
          <Text
            as="span"
            lineHeight="none"
            fontFamily={"normal"}
            fontSize={[0, 1]}
            fontWeight="500"
          >
            {count < 10 ? count : "9+"}
          </Text>
        </Box>
      )}
    </CartCounter>
  );
};

export const CartIconButton = ({
  onClick,
  isLoading,
  count = 0,
  isLarge,
  hasLowStockItem,
  disableAnimation,
  ...rest
}: CartIconButtonProps): JSX.Element => {
  const [canAnimate, setCanAnimate] = useState<boolean>(false);

  useEffect(() => {
    const currentTime = new Date().getTime();
    const cartAnimationExpiryInMinutes = localStorage.getItem(
      "cartAnimationExpiryInMinutes"
    );

    // removed the expiry animation when no items with low stock on cart
    if (cartAnimationExpiryInMinutes && !hasLowStockItem)
      localStorage.removeItem("cartAnimationExpiryInMinutes");

    if (!hasLowStockItem) return;
    if (disableAnimation) {
      setCanAnimate(false);
    }

    if (!cartAnimationExpiryInMinutes) {
      if (!disableAnimation) setCanAnimate(true);
      setCartAnimationExpiryInMinutes(currentTime);
    } else {
      const expiryTime = parseInt(cartAnimationExpiryInMinutes, 10);
      if (currentTime > expiryTime) {
        if (!disableAnimation) setCanAnimate(true);
        setCartAnimationExpiryInMinutes(currentTime);
      }
    }
  }, [disableAnimation, hasLowStockItem]);

  useEffect(() => {
    if (canAnimate) {
      const interval = setInterval(() => {
        const icon = document.getElementById("cartIcon");
        icon.classList.add("shake-animation");

        setTimeout(() => {
          icon.classList.remove("shake-animation");
        }, 1000);
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [canAnimate]);

  const setCartAnimationExpiryInMinutes = (currentTime) => {
    const newExpiryTine = currentTime + 30 * 60 * 1000; //30 minutes cooldown
    localStorage.setItem(
      "cartAnimationExpiryInMinutes",
      newExpiryTine.toString()
    );
  };

  return (
    <CartButton
      {...rest}
      onClick={onClick}
      color="white"
      count={count}
      disabled={isLoading}
    >
      <Box display="flex" alignItems="center" color="white" position="relative">
        <CartIconStyled id="cartIcon" size="25px" />
        {isLarge ? (
          <CartTotalIndicator isLoading={isLoading} count={count} />
        ) : (
          !!count && <CartDotIndicator backgroundColor="brand" />
        )}
      </Box>
    </CartButton>
  );
};
